"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Star, ArrowRight, Quote } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
interface ReviewItem {
  name: string;
  excerpt: string;
  source: string;
  rating: number;
}
const reviewsData: ReviewItem[] = [{
  name: "JPA Roofing",
  excerpt: "We use BMF as our chosen scaffolding firm for all of our roofing projects.",
  source: "Google Review",
  rating: 5
}, {
  name: "Structa Roofing Ltd",
  excerpt: "We will only ever use BMF for our scaffolding needs.",
  source: "Google Review",
  rating: 5
}, {
  name: "Carl Barrie",
  excerpt: "I have used BMF Scaffolding twice now. Once on a new build project and once on an existing home.",
  source: "Google Review",
  rating: 5
}, {
  name: "Norik Grigorian",
  excerpt: "Used BMF scaffolding for a few years now has never let me down.",
  source: "Google Review",
  rating: 5
}];
export default function Reviews() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.075
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="socialproofreviews" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header with Datum Bar */}
        <div className="flex flex-col items-center text-center mb-14 md:mb-20">
          <div className="h-[3px] w-12 bg-primary mb-6 rounded-full" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground max-w-3xl font-default">
            Trusted by homeowners, contractors and businesses
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">
            Reliable scaffolding access delivered safely across Berkshire, Surrey, Hampshire and London.
          </p>
        </div>

        {/* Reviews Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
          {reviewsData.map((review, i) => <motion.div key={review.name} data-index={i} variants={cardVariants} whileHover={shouldReduceMotion ? {} : {
          y: -3
        }} transition={{
          duration: 0.22,
          ease: "easeOut"
        }} className="group flex flex-col justify-between rounded-lg border border-border bg-card p-6 sm:p-8 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
              <div>
                {/* Top Row: Stars and Quote Icon */}
                <div className="flex items-center justify-between gap-2 mb-5">
                  <div className="flex items-center gap-1" aria-label={`Rating: ${review.rating} out of 5 stars`}>
                    {Array.from({
                  length: review.rating
                }).map((_, starIndex) => <Star key={starIndex} className="size-4 fill-chart-5 text-chart-5" aria-hidden="true" />)}
                  </div>
                  <Quote className="size-5 text-muted-foreground/30 transition-colors group-hover:text-primary/40" />
                </div>

                {/* Excerpt */}
                <p className="text-foreground text-sm sm:text-base leading-relaxed mb-6 italic">
                  &ldquo;{review.excerpt}&rdquo;
                </p>
              </div>

              {/* Card Footer: Reviewer Info & Badge */}
              <div className="pt-4 border-t border-border/60 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground tracking-normal font-default">
                    {review.name}
                  </h3>
                </div>
                <Badge variant="outline" className="text-[11px] font-medium bg-muted/50 text-muted-foreground border-border/80">
                  {review.source}
                </Badge>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Bottom Action Link */}
        <div className="mt-12 md:mt-16 flex justify-center">
          <Link to="/TestimonialsPage" className="group inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md py-2 px-3">
            <span>Read all customer reviews</span>
            <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}